<template>
	<div
		v-if="cardType === 'list'"
		class="flex justify-start gap-4 flex-col-reverse lg:flex-row lg:gap-8 border-b border-black/[.1] py-5 items-start"
	>
		<NuxtLink
			:to="`/news/${slug}/`"
			class="text-blue-500 hidden lg:block leading-none hover:!underline text-black rounded-2xl overflow-hidden w-full lg:max-w-[256px] lg:h-[180px] h-[320px]"
		>
			<img
				:src="image"
				:alt="`${slug} image`"
				class="w-full h-full object-cover"
				height="180"
				width="256"
			/>
		</NuxtLink>
		<div class="flex flex-col gap-3 lg:gap-4">
			<NuxtLink
				v-if="title"
				:to="`/news/${slug}/`"
				class="hover:!underline text-black"
			>
				<h3
					class="font-bold text-slate-900 leading-tight text-lg lg:text-2xl"
					v-html="title"
				/>
			</NuxtLink>
			<p
				class="text-slate-400 text-sm leading-none"
				v-html="daysAgo(date, true)"
			></p>

			<div
				class="flex items-center gap-3"
				v-if="stocks.length > 0"
			>
				<StockTicker
					v-for="ticker in stocks"
					:key="ticker.SYMBOL"
					:symbol="ticker.SYMBOL"
					:change="ticker.PC_CHG"
				/>
			</div>
		</div>
	</div>
	<div
		v-else-if="cardType === 'trending-list'"
		class="flex flex-col gap-2 border-b border-white/[.1] py-4"
	>
		<NuxtLink
			to="`/news/${slug}`"
			class="hover:underline"
		>
			<h3
				class="font-bold text-white leading-tight text-lg"
				v-html="title"
			/>
		</NuxtLink>
		<div class="flex gap-2 items-center">
			<p
				class="text-white/[.5] text-sm leading-none"
				v-html="`${reading_time} min read`"
			></p>

			<span class="w-[1px] h-[12px] bg-white/[.5]" />

			<span class="text-white/[.5] text-sm">
				By
				<NuxtLink
					to="`/news/${slug}`"
					class="text-blue-500 leading-none hover:underline"
				>
					Tim Bohen
				</NuxtLink>
			</span>
		</div>
	</div>
	<div
		v-else-if="cardType === 'featured'"
		class="flex flex-col gap-4 w-full"
	>
		<NuxtLink :to="`/news/${slug}`">
			<img
				v-if="cardType !== 'tertiary'"
				:src="image"
				:alt="`${slug} image`"
				class="rounded-2xl"
			/>
		</NuxtLink>

		<div class="flex flex-col gap-4 align-center justify-center">
			<NuxtLink
				:to="`/news/${slug}/`"
				class="hover:!underline text-black w-full"
			>
				<h3
					class="font-bold text-slate-900 leading-tight lg:text-center text-2xl lg:text-4xl lg:px-6"
					v-html="title"
				/>
			</NuxtLink>

			<p
				class="text-black text-base lg:text-xl lg:text-center"
				v-if="description"
			>
				{{ truncateText(description, 280) }}

				<NuxtLink
					:to="`/news/${slug}/`"
					class="text-blue-500 leading-none hover:!underline"
				>
					Read More
				</NuxtLink>
			</p>

			<div class="flex lg:text-center lg:mx-auto gap-2 lg:items-center">
				<p
					class="text-slate-400 text-sm leading-none"
					v-html="daysAgo(date, true)"
				></p>
				<span class="w-[1px] h-[12px] bg-slate-400" />

				<p
					class="text-slate-400 text-sm leading-none"
					v-html="`${reading_time} min read`"
				></p>
			</div>

			<div
				class="flex gap-2 flex-wrap lg:justify-center mt-2"
				v-if="stocks.length > 0"
			>
				<StockTicker
					v-for="ticker in stocks"
					:key="ticker.SYMBOL"
					:symbol="ticker.SYMBOL"
					:change="ticker.PC_CHG"
				/>
			</div>
		</div>
	</div>
	<div
		v-else-if="cardType === 'tertiary'"
		class="flex flex-col gap-4 w-full"
	>
		<div
			class="flex flex-col gap-3"
			:class="{ 'border-b border-black/[.1] pb-4': !noBorder }"
		>
			<NuxtLink
				:to="`/news/${slug}/`"
				v-if="title"
			>
				<h3
					class="font-bold text-slate-900 leading-normal text-lg hover:!underline text-black"
					v-html="title"
				/>
			</NuxtLink>

			<div class="flex gap-2 items-center">
				<span
					class="text-slate-400 text-sm leading-none"
					v-html="`${daysAgo(date, true)}`"
				></span>
			</div>

			<div
				class="flex gap-2 flex-wrap"
				v-if="stocks.length > 0"
			>
				<StockTicker
					v-for="ticker in stocks"
					:key="ticker.SYMBOL"
					:symbol="ticker.SYMBOL"
					:change="ticker.PC_CHG"
				/>
			</div>
		</div>
	</div>
	<div
		v-else
		class="flex flex-col gap-4 w-full"
	>
		<NuxtLink :to="`/news/${slug}`">
			<img
				v-if="cardType !== 'tertiary'"
				:src="image"
				:alt="`${slug} image`"
				class="rounded-2xl"
			/>
		</NuxtLink>

		<div
			class="flex flex-col gap-3"
			:class="{
				'mb-4 pb-4 border-b border-black/[.1]': cardType === 'tertiary'
			}"
		>
			<div
				v-if="['secondary', 'hero'].includes(cardType)"
				class="flex justify-between"
			>
				<h4 :class="`text-blue-500 leading-none font-semibold`">Stock News</h4>
				<div class="hidden lg:flex ml-auto gap-2 items-center">
					<p
						class="text-slate-400 text-sm leading-none"
						v-html="`${reading_time} min read`"
					></p>
					<template v-if="cardType === 'hero'">
						<span class="w-[1px] h-[12px] bg-slate-400" />
						<p
							class="text-slate-400 text-sm leading-none"
							v-html="daysAgo(date, true)"
						></p>
					</template>
				</div>
			</div>
			<NuxtLink
				:to="`/news/${slug}/`"
				class="hover:!underline text-black"
			>
				<h3
					class="font-bold text-slate-900 leading-tight"
					:class="{
						'text-2xl lg:text-3xl': cardType === 'hero',
						'text-lg': ['secondary', 'tertiary'].includes(cardType)
					}"
					v-html="title"
				/>
			</NuxtLink>

			<div
				v-if="cardType === 'hero'"
				class="flex lg:hidden gap-2 items-center"
			>
				<p
					class="text-slate-400 text-sm leading-none"
					v-html="`${reading_time} min read`"
				></p>
				<span class="w-[1px] h-[12px] bg-slate-400" />
				<p
					class="text-slate-400 text-sm leading-none"
					v-html="formatDateForNews(date)"
				></p>
			</div>
			<p
				v-if="cardType === 'hero'"
				class="text-black text-base lg:text-lg"
			>
				{{ truncateText(description, 190) }}

				<NuxtLink
					:to="`/news/${slug}/`"
					class="text-blue-500 leading-none hover:underline"
				>
					Read More
				</NuxtLink>
			</p>
			<div
				v-if="['secondary', 'tertiary'].includes(cardType)"
				class="flex gap-2 items-center"
			>
				<template v-if="cardType === 'tertiary'">
					<p
						class="text-slate-400 text-sm leading-none"
						v-html="`${reading_time} min read`"
					></p>
					<span class="w-[1px] h-[12px] bg-slate-400" />
				</template>
				<p
					class="text-slate-400 text-sm leading-none"
					v-html="formatDateForNews(date)"
				></p>
			</div>
		</div>
	</div>
</template>

<script setup>
import axios from "axios";
import dayjs from "dayjs";
import MD5 from "crypto-js/md5";
import { daysAgo } from "~/utils";

const $config = useRuntimeConfig();
const stocks = ref([]);

const props = defineProps({
	title: {
		type: String
		// required: true
	},
	content: {
		type: String
		// required: true
	},
	description: {
		type: String

		// required: true
	},

	image: {
		type: String
		// required: true
	},
	slug: {
		type: String
		// required: true
	},
	reading_time: {
		type: Number
	},
	date: {
		type: String
		// required: true
	},
	cardType: {
		type: String,
		default: "hero"
	},
	theme: {
		type: String,
		default: "light"
	},
	noBorder: {
		type: Boolean,
		default: false
	}
});

const title = computed(() => {
	switch (props.cardType) {
		case "hero":
		case "list":
			return truncateText(props.title, 80);
		case "featured":
		case "tertiary":
			return props.title;

		default:
			return truncateText(props.title, 56);
	}
});

const getTickers = async (hostname, content) => {
	if (!content) {
		return;
	}

	const { data } = await axios.get(
		`${hostname}.netlify/functions/get-tickers?content=${encodeURIComponent(content)}`
	);

	if (data.length === 0) {
		return;
	}

	const date = dayjs(Date.now()).format("ddd, D MMM YYYY");

	const secretToken = MD5(date).toString();

	const headers = {
		secretToken
	};

	const getStocksInfo = data.map(async ({ symbol }) => {
		const data = {
			filter: `SYMBOL IN ('${symbol}')`,
			prevDay: false,
			fields:
				"VOLUME, TRADES, FLOAT, SHORT_INTEREST,SHORT_INTEREST_RATIO, SYMBOL, NAME, LAST, NET_CHG, PC_CHG, MARKET_CAP, HIGH_52W, LOW_52W, LOW, HIGH, DAY_RANGE, MARKET"
		};

		return await axios.post(
			"https://app02.stockstotrade.com:8094/v1.1/symbols",
			data,
			{
				headers
			}
		);
	});

	Promise.all(getStocksInfo).then((res) => {
		res.forEach((r) => {
			if (r.data[0]) {
				stocks.value.push(r.data[0]);
			}
		});
	});

	return data;
};

onMounted(() => {
	if (process.client && props.title && props.description) {
		getTickers(
			$config.public.current_hostname,
			(props.title ?? "") + "/// " + props.description
		);
	}
});
</script>
